import React, { useState, useEffect } from 'react';
import ParamsCard from '../../../../components/Card/Params';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import axios from 'axios';
import ConfirmationModal from '../../../../components/Modals/ConfirmationModal';
import { useParams } from 'react-router-dom';
import { X, Power, RefreshCw, Download, Server, GitBranch, Activity, CheckCircle, Clock } from 'lucide-react';

const TaskModal = ({ isOpen, onClose, onSubmit, currentValue }) => {
  const [taskCount, setTaskCount] = useState(currentValue);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(taskCount);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          <div className="bg-gray-900 px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg font-medium text-white">
              Update Desired Tasks
            </h3>
            <button
              onClick={onClose}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-gray-700 text-sm font-medium block mb-1">
                  Number of Tasks
                </label>
                <input
                  type="number"
                  min={0}
                  value={taskCount}
                  onChange={(e) => setTaskCount(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Update Tasks
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

const General = ({ serviceBasicInfo }) => {
  const { serviceId } = useParams();
  const isSidebarOpen = useSelector(state => state.app.isSidebarOpen);
  const [loading, setLoading] = useState(false);
  const [serviceInfo, setServiceInfo] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    isVisible: false,
    action: null,
    title: '',
    confirmText: '',
    confirmButtonText: '',
    actionType: '',
    loading: false
  });

  useEffect(() => {
    if (serviceBasicInfo?.status === 'Active') {
      fetchServiceInfo();
    }
  }, [serviceBasicInfo?.status]);

  const clusterName = serviceBasicInfo?.clusterName;
  const serviceName = serviceBasicInfo?.serviceName !== ""? serviceBasicInfo?.serviceName : `${serviceBasicInfo?.gitHubRepo}-${serviceBasicInfo?.branchUsed}`;

  const fetchServiceInfo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`https://cloudify-prod.wizr.in/${serviceId}/${clusterName}/${serviceName}/info`);
      setServiceInfo(response.data);
    } catch (error) {
      console.error('Error fetching service info:', error);
      message.error('Failed to fetch service information');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'Active': { icon: <CheckCircle className="h-4 w-4 mr-1" />, color: 'text-green-600 bg-green-50 border-green-200' },
      'Inactive': { icon: <Power className="h-4 w-4 mr-1" />, color: 'text-gray-600 bg-gray-50 border-gray-200' },
      'Pending': { icon: <Clock className="h-4 w-4 mr-1" />, color: 'text-yellow-600 bg-yellow-50 border-yellow-200' }
    };
    
    const defaultStatus = { icon: <Activity className="h-4 w-4 mr-1" />, color: 'text-blue-600 bg-blue-50 border-blue-200' };
    const statusStyle = statusMap[status] || defaultStatus;
    
    return (
      <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${statusStyle.color}`}>
        {statusStyle.icon}
        {status}
      </div>
    );
  };

  const baseInfo = [
    {
      title: "Status",
      value: serviceBasicInfo?.status,
      customDisplay: serviceBasicInfo?.status ? getStatusBadge(serviceBasicInfo.status) : '-',
      isCopy: true,
      icon: <Activity className="w-5 h-5 text-indigo-500" />
    },
    {
      title: "Github Repository",
      value: serviceBasicInfo?.gitHubRepo,
      isCopy: true,
      icon: <GitBranch className="w-5 h-5 text-indigo-500" />
    },
    {
      title: "Branch",
      value: serviceBasicInfo?.branchUsed,
      isCopy: true,
      icon: <GitBranch className="w-5 h-5 text-indigo-500" />
    },
    {
      title: "Desired Tasks",
      value: serviceInfo?.['Desired Tasks'] || '-',
      isCopy: true,
      isEdit: true,
      icon: <Server className="w-5 h-5 text-indigo-500" />
    },
    {
      title: "Running Tasks",
      value: serviceInfo?.['Running Tasks'] || '-',
      isCopy: true,
      icon: <Server className="w-5 h-5 text-indigo-500" />
    },
    {
      title: "Pending Tasks",
      value: serviceInfo?.['Pending Tasks'] || '-',
      isCopy: true,
      icon: <Clock className="w-5 h-5 text-indigo-500" />
    }
  ];

  const handleServiceAction = async (action) => {
    let title = '';
    let confirmText = '';
    let confirmButtonText = '';
    let actionType = '';

    switch (action) {
      case 'stop':
        title = 'Stop Service?';
        confirmText = 'Are you sure you want to stop this service?';
        confirmButtonText = 'Stop';
        actionType = 'stop';
        break;
      case 'start':
        title = 'Start Service?';
        confirmText = 'Are you sure you want to start this service?';
        confirmButtonText = 'Start';
        actionType = 'start';
        break;
      default:
        throw new Error('Invalid action');
    }

    setConfirmModalConfig({
      isVisible: true,
      action,
      title,
      confirmText,
      confirmButtonText,
      actionType,
      loading: false
    });
  };

  const handleConfirm = async () => {
    const { action } = confirmModalConfig;
    setConfirmModalConfig(prev => ({ ...prev, loading: true }));

    try {
      let endpoint;
      let payload = {
        cluster_name: serviceBasicInfo.clusterName,
        service_name: serviceBasicInfo.serviceName
      };

      switch (action) {
        case 'stop':
          endpoint = `/api/server/${serviceBasicInfo.serverId}/stop_service`;
          break;
        case 'start':
          endpoint = `/api/server/${serviceBasicInfo.serverId}/update_desired_count`;
          payload.desired_count = 1;
          break;
        default:
          throw new Error('Invalid action');
      }

      await axios.post(endpoint, payload);
      message.success(`Service ${action} successful`);
      if (action === 'start') {
        fetchServiceInfo();
      }
    } catch (error) {
      console.error('Error:', error);
      message.error(`Failed to ${action} service: ${error.message}`);
    } finally {
      setConfirmModalConfig({ isVisible: false, action: null, title: '', confirmText: '', confirmButtonText: '', loading: false });
    }
  };

  const handleUpdateDesiredTasks = async (newTaskCount) => {
    try {
      setLoading(true);
      await axios.post(`/api/server/${serviceBasicInfo.serverId}/update_desired_count`, {
        cluster_name: serviceBasicInfo.clusterName,
        service_name: serviceBasicInfo.serviceName,
        desired_count: parseInt(newTaskCount)
      });
      
      message.success('Desired tasks updated successfully');
      fetchServiceInfo();
      setIsTaskModalOpen(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Error updating desired tasks:', error);
      message.error('Failed to update desired tasks');
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex justify-between items-center mb-5 border-b border-gray-100 pb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Service Configuration</h2>
              <p className="text-gray-500 text-sm mt-1">Manage and monitor your service settings</p>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => handleServiceAction(serviceBasicInfo?.status === 'Active' ? 'stop' : 'start')}
                className={`inline-flex items-center px-4 py-2 ${
                  serviceBasicInfo?.status === 'Active' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-green-600 hover:bg-green-700'
                } text-white rounded-lg transition-colors duration-200 font-medium shadow-sm gap-2`}
              >
                <Power className="h-4 w-4" />
                {serviceBasicInfo?.status === 'Active' ? 'Stop' : 'Start'}
              </button>
            </div>
          </div>

          <div className={`grid gap-5 ${
            isSidebarOpen 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
              : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {baseInfo.map((item, index) => (
              <ParamsCard
                key={index}
                title={item.title}
                value={item.value}
                customDisplay={item.customDisplay}
                isCopy={item.isCopy}
                isEdit={item.isEdit}
                icon={item.icon}
                onEdit={() => {
                  if (item.title === "Desired Tasks") {
                    setEditingTask(item.value === '-' ? 1 : item.value);
                    setIsTaskModalOpen(true);
                  }
                }}
              />
            ))}
          </div>
        </div>

        <ConfirmationModal
          isVisible={confirmModalConfig.isVisible}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmModalConfig({ isVisible: false, action: null, title: '', confirmText: '', confirmButtonText: '', loading: false })}
          title={confirmModalConfig.title}
          confirmText={confirmModalConfig.confirmText}
          confirmButtonText={confirmModalConfig.confirmButtonText}
          actionType={confirmModalConfig.actionType}
          loading={confirmModalConfig.loading}
        />

        <TaskModal
          isOpen={isTaskModalOpen}
          onClose={() => {
            setIsTaskModalOpen(false);
            setEditingTask(null);
          }}
          onSubmit={handleUpdateDesiredTasks}
          currentValue={editingTask}
        />
      </div>
    </>
  );
};

export default General;